import React from 'react';
import If from 'components/if-statement';
import FilePreview from './components/file-preview';
import { DropZone, HiddenInput, Label } from 'components/file-input/style';
import { useTranslation } from 'i18n';
import { notEmpty } from 'utils/js';
import { noop } from 'lodash';
import { Styles } from 'utils/types/react';

type AcceptType = 'pdf' | 'png' | 'jpeg' | 'jpg' | "mp4";

export interface FileInputProps {
  name?: string;
  isDisabled?: boolean;
  onChange?: (file) => void;
  accept?: AcceptType | AcceptType[];
  label?: string;
  style?: Styles;
  className?: string;
  invalid?: boolean;
}

export const acceptTypeMap = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpg',
  jpeg: 'image/jpeg',
  mp4: 'video/mp4',
};

const defaultProps = {};

function FileInput({
  name,
  isDisabled = false,
  onChange = noop,
  accept,
  label,
  style,
  className,
  ...props
}: FileInputProps) {
  const fileInput = React.useRef<any>(null);
  const [selectedFile, setSelectedFile] = React.useState<any>(null);
  const { t } = useTranslation();

  label = label ?? t('Select or drag file');

  function openFileDialog() {
    if (isDisabled) {
      return;
    }

    fileInput.current.click();
  }

  function onChangeHandler(event) {
    if (isDisabled) {
      return;
    }

    const [file] = event.target.files;

    onChange(file);

    setSelectedFile(file);
  }

  function onDragOver(event) {
    event.preventDefault();

    if (isDisabled) {
      return;
    }
  }

  function onDragLeave() {}

  function onDrop(event) {
    event.preventDefault();

    if (isDisabled) return;

    const [file] = event.dataTransfer.files;

    onChange(file);

    setSelectedFile(file);
  }

  function getAcceptFileValues(): undefined | string {
    if (accept) {
      if (Array.isArray(accept)) {
        if (notEmpty(accept)) {
          return accept
            .map((value) => acceptTypeMap[value])
            .filter(Boolean)
            .join(',');
        }
      } else {
        return acceptTypeMap[accept];
      }
    }

    return undefined;
  }

  return (
    <DropZone
      isDisabled={isDisabled}
      onClick={openFileDialog}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={style}
      className={className}
      {...props}>
      <If condition={selectedFile}>
        <If.Then>
          <FilePreview file={selectedFile} />
        </If.Then>
        <If.Else>
          <Label $invalid={props.invalid}>{label}</Label>
        </If.Else>
      </If>
      <HiddenInput
        ref={fileInput}
        name={name}
        type="file"
        accept={getAcceptFileValues()}
        onChange={onChangeHandler}
      />
      {accept}
      {getAcceptFileValues()}
    </DropZone>
  );
}

FileInput.defaultProps = defaultProps;

export default FileInput;
